import { flow, pipe } from 'fp-ts/lib/function';
import { User } from '@/entities/user';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { detailCodec, formCodec, listCodec } from './codec';
import {
  addVersionForUpdate,
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  decode,
  decodeId,
  decodeVersioned,
  fetchApi,
  setBody,
  setQuery,
  Versioned,
} from '../..';
import { encodeUserQuery, UserQuery, FilterUserQuery, encodeFilterUserQuery } from './query';
import * as q from '@/services/api/request/query';
import { filterCodec } from '@/services/api/resources/filter';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';
import { configuration, timeWindow } from '@/services/api/auth/notificationSettings';
import { ReminderConfiguration, TimeWindow } from '@/entities/auth/reminderConfiguration';

const baseUrl = '/v2/users';
const filterUrl = '/v2/filters/users';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;
const notificationSettingsUrl = (id: number): string => `${baseUrl}/${id}/notification-settings`;
const timeSettingsUrl = (id: number): string => `${baseUrl}/${id}/time-window-settings`;

export const list = (query: UserQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeUserQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const filter = (query: FilterUserQuery = {}) => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeFilterUserQuery(query))),
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const create = (user: User) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(user))),
  chain(fetchApi),
  chain(decodeId),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(detailCodec)),
);

export const update = (id: number, user: Versioned<User>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(user.version)),
  map(setBody(formCodec.encode(user.data))),
  chain(fetchApi),
);

export const updatePassword = (id: number, password: string) => pipe(
  `${baseUrl}/${id}/password`,
  createAuthorizedPutRequest,
  map(setBody({ password })),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const emailCheck = (query?: { email: string }) => pipe(
  `${baseUrl}/email/check`,
  createAuthorizedGetRequest,
  map(setQuery({ email: query?.email ? q.string(query.email) : '' })),
  chain(fetchApi),
);

export const inviteUsers = (emails: Array<string>) => pipe(
  `${baseUrl}/invite`,
  createAuthorizedPostRequest,
  map(setBody({ emails })),
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);

export const getUserNotificationSettings = flow(
  notificationSettingsUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(configuration)),
);

export const updateUserNotificationSettings = (notificationSettings: ReminderConfiguration, id:number, version: string) => pipe(
  id,
  notificationSettingsUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(version)),
  map(setBody(notificationSettings)),
  chain(fetchApi),
);

export const getUserTimeSettings = flow(
  timeSettingsUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(timeWindow)),
);

export const updateUserTimeWindowSettings = (timeWindowSettings : TimeWindow[], id:number, version: string) => pipe(
  id,
  timeSettingsUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(version)),
  map(setBody(timeWindowSettings)),
  chain(fetchApi),
);
